import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, Trash2, ExternalLink } from "lucide-react";

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [logo, setLogo] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);

  const load = () => api.get("/sponsors").then((res) => setSponsors(res.data));

  useEffect(() => {
    load();
  }, []);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogo(data.url);
      toast.success("Logo caricato");
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const add = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error("Inserisci nome e sito web dell'azienda");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sponsors", { name: name.trim(), url: url.trim(), logo: logo || null });
      setName("");
      setUrl("");
      setLogo("");
      await load();
      toast.success("Azienda aggiunta");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sponsors/${id}`);
      setSponsors((s) => s.filter((x) => x.id !== id));
      toast.success("Azienda rimossa");
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors">
            <ArrowLeft size={15} strokeWidth={1.5} /> Torna
          </Link>
          <Link to="/sponsor" target="_blank" className="label-ui text-[#57534E] hover:text-[#9F583F] transition-colors inline-flex items-center gap-2">
            Vedi pagina Sponsor <ExternalLink size={13} strokeWidth={1.5} />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Spazi pubblicitari</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-3">Gestisci le aziende</h1>
        <p className="font-body text-[#57534E] mb-10 max-w-2xl">
          Ci sono 50 spazi disponibili nella pagina Sponsor. Ogni azienda che aggiungi occupa
          uno spazio; i restanti restano liberi come "Il tuo logo qui".
        </p>

        {/* Add form */}
        <div className="border border-[#E7E5E4] p-6 mb-12 bg-[#F2F0EB]">
          <h2 className="font-display text-2xl text-[#1C1917] mb-6">Aggiungi un'azienda</h2>
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
            {logo ? (
              <div className="relative w-32">
                <img src={mediaUrl(logo)} alt="Logo" className="w-32 h-32 object-contain border border-[#E7E5E4] bg-white p-2" data-testid="sponsor-logo-preview" />
                <button onClick={() => setLogo("")} className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-[#1C1917] text-white rounded-full text-xs">✕</button>
              </div>
            ) : (
              <button
                onClick={() => logoInput.current?.click()}
                data-testid="upload-sponsor-logo"
                className="w-32 h-32 border border-dashed border-[#E7E5E4] bg-white flex flex-col items-center justify-center gap-2 text-[#57534E] hover:border-[#9F583F] hover:text-[#9F583F] transition-colors"
              >
                {uploading ? <Loader2 size={22} className="animate-spin" /> : (<><ImagePlus size={22} strokeWidth={1.5} /><span className="label-ui">Logo</span></>)}
              </button>
            )}
            <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={handleLogo} />

            <div>
              <label className="label-ui text-[#57534E] block mb-2">Nome azienda</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="sponsor-name-input"
                placeholder="Es. Caffè Roma"
                className="font-body rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-6"
              />
              <label className="label-ui text-[#57534E] block mb-2">Sito web (URL)</label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="sponsor-url-input"
                placeholder="es. www.esempio.it"
                className="font-body rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-6"
              />
              <Button
                onClick={add}
                disabled={saving}
                data-testid="add-sponsor-button"
                className="rounded-none bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-6 py-5 h-auto transition-colors"
              >
                {saving ? "Salvataggio…" : "Aggiungi azienda"}
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        <h2 className="font-display text-2xl text-[#1C1917] mb-6">Aziende attuali ({sponsors.length}/50)</h2>
        {sponsors.length === 0 ? (
          <p className="font-body italic text-[#57534E] border-t border-[#E7E5E4] pt-8">
            Nessuna azienda ancora. Gli spazi appariranno come "Il tuo logo qui".
          </p>
        ) : (
          <div className="border-t border-[#E7E5E4]">
            {sponsors.map((s) => (
              <div key={s.id} data-testid={`admin-sponsor-${s.id}`} className="flex items-center justify-between gap-4 py-4 border-b border-[#E7E5E4]">
                <div className="flex items-center gap-4 min-w-0">
                  {s.logo ? (
                    <img src={mediaUrl(s.logo)} alt={s.name} className="w-14 h-14 object-contain border border-[#E7E5E4] bg-white p-1" />
                  ) : (
                    <div className="w-14 h-14 border border-[#E7E5E4] bg-white flex items-center justify-center font-display text-lg text-[#9F583F]">{s.name.charAt(0)}</div>
                  )}
                  <div className="min-w-0">
                    <p className="font-display text-xl text-[#1C1917] truncate">{s.name}</p>
                    <p className="font-ui text-xs text-[#57534E] truncate">{s.url}</p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-sponsor-${s.id}`} className="w-10 h-10 flex items-center justify-center text-[#57534E] hover:text-red-600 transition-colors" title="Elimina">
                      <Trash2 size={17} strokeWidth={1.5} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-none bg-[#F9F8F6] border-[#E7E5E4]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl">Rimuovere "{s.name}"?</AlertDialogTitle>
                      <AlertDialogDescription className="font-body">Lo spazio tornerà disponibile come "Il tuo logo qui".</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none label-ui">Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(s.id)} data-testid="confirm-delete-sponsor" className="rounded-none bg-red-700 hover:bg-red-800 label-ui">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
